/* istanbul ignore file */
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  RouteRecordRaw,
  Router,
  createMemoryHistory,
  createRouter,
  createWebHistory,
} from 'vue-router';

import { DEFAULT_LANGUAGE, setLocaleBeforeRouteChange } from '@/i18n-setup';

import { createAuth0Instance } from '@/helpers/Auth/authClient';
import { getCurrentPlatform, isServer } from '@/helpers/Platform/Platform';
import { getLoginCallbackUrl } from '@/helpers/Route/routeHelper';
import { Auth0Plugin } from '@auth0/auth0-vue';
import getLandingPageRoute from './routes/landingPage';
import getLoginPageRoute from './routes/loginPage';
import getValidationErrorRoute from './routes/validationError';

export function getAllRoutes(): RouteRecordRaw[] {
  return [
    ...getValidationErrorRoute,
    ...getLandingPageRoute(),
    ...getLoginPageRoute(),
    {
      path: getLoginCallbackUrl(false),
      name: 'LoginCallback',
      component: () => import('@/pages/Login/LoginCallback.vue'),
      meta: {
        getLanguageSwitchLink(route: RouteRecordRaw, locale: HgSupportedLanguage) {
          return {
            name: route.name,
            params: {
              locale,
            },
          };
        },
      },
    },
    {
      path: '/:pageNotFound*',
      name: 'pageNotFound',
      component: () => import('@smg-real-estate/domus/HgPageNotFound.vue'),
      props: () => ({
        pageLayoutCssClass: 'layout-container',
        theme: getCurrentPlatform(),
      }),
      meta: {
        getLanguageSwitchLink(route: RouteRecordRaw) {
          return {
            path: route.path,
          };
        },
      },
    },
  ];
}

// Create router as a factory function to ensure fresh instance each time
// This is critical for SSR - each request needs a fresh router instance
export function createAppRouter(): Router {
  const BASE_PATH = import.meta.env.VUE_BASE_PATH || '/';
  return createRouter({
    history: import.meta.env.SSR ? createMemoryHistory(BASE_PATH) : createWebHistory(BASE_PATH),
    routes: getAllRoutes(),
  });
}

export function initGuards(router: Router) {
  router.beforeEach(async (to, _, next) => {
    const locale =
      ((to?.params?.locale ?? to?.meta?.locale) as HgSupportedLanguage) || DEFAULT_LANGUAGE;
    await setLocaleBeforeRouteChange(locale);

    if (isServer()) {
      next();
      return;
    }

    const { loginWithRedirect, isAuthenticated, checkSession } =
      createAuth0Instance() as Auth0Plugin;

    try {
      await checkSession();
    } catch (error) {
      // ignore
    }

    if (to.meta?.requiresAuth && !isAuthenticated.value) {
      await loginWithRedirect({
        authorizationParams: {
          ui_locales: locale,
        },
        appState: {
          target: to.fullPath,
        },
      });
      next(false);
    }

    next();
  });
}
